import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { MessageSquare, Instagram, Facebook, Settings, Save, Loader2, Sparkles, Send, Copy, Check, Music, Trash2, AlertTriangle, CheckCircle2, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SocialComments() {
  const [settings, setSettings] = useState({
    enabled: false,
    auto_reply: true,
    use_ai: true,
    reply_template: '¡Gracias por tu comentario! 💙 Te invitamos a escribirnos por WhatsApp para más info.',
    invite_to_dm: true,
    invite_to_whatsapp: true,
    whatsapp_number: '',
    keywords_to_reply: [],
    // Notification settings
    notify_on_reply: true,
    daily_summary: true,
    notification_phone: ''
  });
  const [tiktokSettings, setTiktokSettings] = useState({
    enabled: false,
    username: '',
    notify_on_comment: true,
    notify_whatsapp: ''
  });
  const [comments, setComments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [keywordInput, setKeywordInput] = useState('');
  
  // TikTok helper
  const [tiktokComment, setTiktokComment] = useState('');
  const [tiktokUsername, setTiktokUsername] = useState('');
  const [tiktokSuggestion, setTiktokSuggestion] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchComments();
    fetchTiktokSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const response = await fetch(`${API}/api/clinic/${clinicId}/comment-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchTiktokSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const response = await fetch(`${API}/api/clinic/${clinicId}/tiktok-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTiktokSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const response = await fetch(`${API}/api/clinic/${clinicId}/social-comments?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const deleteComment = async (commentId, deleteFromPlatform = false) => {
    const message = deleteFromPlatform 
      ? '¿Eliminar este comentario de Instagram/Facebook Y de la lista?' 
      : '¿Eliminar este comentario solo de la lista? (El comentario seguirá visible en Instagram/Facebook)';
    if (!window.confirm(message)) return;
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const url = `${API}/api/clinic/${clinicId}/social-comments/${commentId}${deleteFromPlatform ? '?delete_from_platform=true' : ''}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setComments(comments.filter(c => c.comment_id !== commentId));
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      
      // Save comment settings
      await fetch(`${API}/api/clinic/${clinicId}/comment-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      // Save TikTok settings
      await fetch(`${API}/api/clinic/${clinicId}/tiktok-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tiktokSettings)
      });
      
      alert('✅ Configuración guardada');
    } catch (error) {
      alert('❌ Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !settings.keywords_to_reply.includes(keywordInput.trim())) {
      setSettings({
        ...settings,
        keywords_to_reply: [...settings.keywords_to_reply, keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setSettings({
      ...settings,
      keywords_to_reply: settings.keywords_to_reply.filter(k => k !== keyword)
    });
  };

  const generateTiktokReply = async () => {
    if (!tiktokComment.trim()) return;
    
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const response = await fetch(`${API}/api/clinic/${clinicId}/tiktok-comment-helper`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          comment: tiktokComment,
          username: tiktokUsername
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setTiktokSuggestion(data.suggestion);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(tiktokSuggestion);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const webhookUrl = 'https://aimedplanner.com/api/webhook/comments';

  return (
    <DashboardLayout title="Comentarios Redes Sociales">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-orange-500 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            Comentarios con IA
          </h1>
          <p className="text-gray-600 mt-1">Responde automáticamente comentarios en Facebook e Instagram</p>
        </div>

        {/* Guía: Comentarios públicos de Facebook e Instagram */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Info className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-lg text-gray-900">Comentarios públicos de Facebook e Instagram</h2>
              <p className="text-gray-600 text-sm mt-1">
                Los comentarios públicos son distintos de los mensajes privados. Esta sección gestiona comentarios de posts, reels o anuncios; 
                <strong> Instagram/Facebook Messages</strong> gestiona DMs.
              </p>
            </div>
          </div>

          {/* Checklist de requisitos */}
          <div className="bg-white rounded-lg p-4 border">
            <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              Checklist de configuración
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                'Facebook Page conectada',
                'Instagram profesional conectado a la página',
                'App Meta con permisos correctos',
                'Webhook de comentarios activo'
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                  <div className="w-4 h-4 border border-gray-300 rounded flex-shrink-0"></div>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-3 border-t">
              <h4 className="font-medium text-gray-700 text-sm mb-2">Permisos requeridos en Meta App:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {[
                  'pages_read_engagement',
                  'pages_manage_engagement',
                  'instagram_business_basic o instagram_basic',
                  'instagram_business_manage_comments o instagram_manage_comments'
                ].map((perm, idx) => (
                  <code key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-700">{perm}</code>
                ))}
              </div>
            </div>

            <div className="mt-4 pt-3 border-t">
              <h4 className="font-medium text-gray-700 text-sm mb-2">Verificación final:</h4>
              <div className="space-y-1">
                {[
                  'Probar comentario en post propio',
                  'Confirmar que aparece en Comentarios IA',
                  'Confirmar que no responde duplicado'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                    <div className="w-4 h-4 border border-gray-300 rounded flex-shrink-0"></div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Advertencia */}
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">⚠️ Importante: Los comentarios son públicos</p>
              <p className="text-amber-700 text-sm mt-1">
                Usa respuestas más conservadoras que en DMs. <strong>No confirmes citas ni disponibilidad en comentarios.</strong> 
                Redirige a WhatsApp o DM para información sensible.
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 border-b">
          {[
            { id: 'settings', label: 'Configuración', icon: Settings },
            { id: 'comments', label: 'Comentarios', icon: MessageSquare }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-4 font-medium flex items-center gap-2 ${activeTab === tab.id ? 'border-b-2 border-pink-500 text-pink-600' : 'text-gray-500'}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Facebook/Instagram Settings */}
            <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
              <div className="flex items-center gap-3">
                <Facebook className="w-6 h-6 text-blue-600" />
                <Instagram className="w-6 h-6 text-pink-600" />
                <h2 className="font-semibold text-lg">Facebook e Instagram</h2>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 mb-2 font-medium">Webhook para comentarios:</p>
                <code className="text-xs bg-white px-2 py-1 rounded">{webhookUrl}</code>
                <p className="text-xs text-blue-600 mt-2">
                  Configura este webhook en Meta → Webhooks → Suscribir a "feed" y "comments"
                </p>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Activar respuestas automáticas</p>
                  <p className="text-sm text-gray-500">Responder comentarios con IA</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.enabled}
                    onChange={(e) => setSettings({...settings, enabled: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Usar IA para respuestas</p>
                  <p className="text-sm text-gray-500">Respuestas personalizadas vs template fijo</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.use_ai}
                    onChange={(e) => setSettings({...settings, use_ai: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-600"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template de respuesta (si IA está desactivada)</label>
                <textarea
                  value={settings.reply_template}
                  onChange={(e) => setSettings({...settings, reply_template: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-pink-500 bg-white text-gray-900"
                  placeholder="¡Gracias por tu comentario! 💙"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp para invitaciones</label>
                <input
                  type="tel"
                  value={settings.whatsapp_number}
                  onChange={(e) => setSettings({...settings, whatsapp_number: e.target.value})}
                  placeholder="5549168798"
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-pink-500 bg-white text-gray-900"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Invitar a WhatsApp</p>
                  <p className="text-sm text-gray-500">Incluir link de WhatsApp en respuestas</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.invite_to_whatsapp}
                    onChange={(e) => setSettings({...settings, invite_to_whatsapp: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-600"></div>
                </label>
              </div>

              {/* Notification Settings */}
              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium text-gray-700 mb-3">📬 Notificaciones</h3>
                
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg mb-3">
                  <div>
                    <p className="font-medium text-green-800">Notificar al responder</p>
                    <p className="text-sm text-green-600">Recibe WhatsApp cuando se responda un comentario</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.notify_on_reply}
                      onChange={(e) => setSettings({...settings, notify_on_reply: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg mb-3">
                  <div>
                    <p className="font-medium text-blue-800">Resumen diario (9 PM)</p>
                    <p className="text-sm text-blue-600">Estadísticas de comentarios del día</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.daily_summary}
                      onChange={(e) => setSettings({...settings, daily_summary: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp para notificaciones</label>
                  <input
                    type="tel"
                    value={settings.notification_phone}
                    onChange={(e) => setSettings({...settings, notification_phone: e.target.value})}
                    placeholder="5215549168798 (deja vacío para usar el de la clínica)"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Keywords Filter */}
            <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
              <h2 className="font-semibold text-lg">Filtro de Palabras Clave</h2>
              <p className="text-gray-600 text-sm">
                Si agregas palabras clave, solo se responderán comentarios que las contengan. 
                Deja vacío para responder a todos los comentarios.
              </p>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                  placeholder="Ej: precio, cita, horario"
                  className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500"
                />
                <button
                  onClick={addKeyword}
                  className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700"
                >
                  Agregar
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {settings.keywords_to_reply.map((keyword, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm"
                  >
                    {keyword}
                    <button
                      onClick={() => removeKeyword(keyword)}
                      className="ml-2 text-pink-500 hover:text-pink-700"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {settings.keywords_to_reply.length === 0 && (
                  <p className="text-gray-400 text-sm">Sin filtro - se responderán todos los comentarios</p>
                )}
              </div>

              <button
                onClick={saveSettings}
                disabled={saving}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-orange-500 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" /> Guardar Configuración</>}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <h3 className="font-semibold">Comentarios Recientes</h3>
              <button
                onClick={fetchComments}
                className="text-sm text-pink-600 hover:underline"
              >
                Actualizar
              </button>
            </div>
            <div className="divide-y max-h-[600px] overflow-y-auto">
              {comments.map(comment => (
                <div key={comment.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${comment.platform === 'instagram' ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-blue-500'}`}>
                      {comment.platform === 'instagram' ? <Instagram className="w-5 h-5 text-white" /> : <Facebook className="w-5 h-5 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{comment.sender_name}</span>
                        <span className="text-xs text-gray-400">{new Date(comment.created_at).toLocaleString('es-MX')}</span>
                        {comment.replied && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Respondido</span>
                        )}
                        <div className="ml-auto flex gap-1">
                          <button
                            onClick={() => deleteComment(comment.comment_id, false)}
                            className="text-gray-400 hover:text-orange-500 p-1"
                            title="Ocultar de la lista"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteComment(comment.comment_id, true)}
                            className="text-gray-400 hover:text-red-600 p-1"
                            title="Borrar de Instagram/Facebook"
                          >
                            <Trash2 className="w-4 h-4 fill-current" />
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-700 mt-1">{comment.message}</p>
                      {comment.reply && (
                        <div className="mt-2 p-2 bg-gray-100 rounded-lg">
                          <p className="text-sm text-gray-600">↩️ {comment.reply}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <div className="p-12 text-center text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay comentarios aún</p>
                  <p className="text-sm mt-1">Los comentarios aparecerán aquí cuando se reciban</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
